import { Disclosure, Transition } from '@headlessui/react';
import dayjs, { Dayjs } from 'dayjs';
import capitalize from 'lodash/capitalize';
import omit from 'lodash/omit';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useRegisterShareholderList } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import { transferTabFilters } from '@/modules/issuer-dashboard/components/operation-tabs/IssuerTransfersTab.tsx';
import { useIssuerRegisterClassListQuery } from '@/modules/issuer-dashboard/queries/registers.tsx';
import { CalendarInput, Select, SelectOption } from 'ui';
import { getDisabledDate } from 'ui/utils';
import { TransactionStatus } from 'api';

interface IssuerTransfersFilterProps {
  filters: transferTabFilters;
  setFilters: (cb: (filters: transferTabFilters) => transferTabFilters) => void;
}

const statusOptions = Object.values(TransactionStatus).map(value => ({
  label: capitalize(value),
  value,
}));

const IssuerTransfersFilters = ({ filters, setFilters }: IssuerTransfersFilterProps) => {
  const [params] = useSearchParams();

  const {
    status,
    register_class_id,
    general_shareholder_id,
    from_shareholder_id,
    to_shareholder_id,
    from_requested_at_date,
    to_requested_at_date,
    from_confirmation_date,
    to_confirmation_date,
  } = filters;

  const registerId = +(params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '0');

  const { data: registerClassesList, isLoading: isRegisterClassesLoading } =
    useIssuerRegisterClassListQuery({}, Number(registerId));

  const registerClassesOptions = useMemo(
    () =>
      (registerClassesList?.registers || []).map(({ id, class_name }) => ({
        label: class_name,
        value: id,
      })),
    [registerClassesList?.registers],
  );

  const { data: registerShareholderList, isLoading: isRegisterShareholderListLoading } =
    useRegisterShareholderList({ register_id: registerId }, { disabled: !registerId });

  const registerShareholderListOptions = useMemo<SelectOption<number>[]>(
    () =>
      registerShareholderList?.shareholders?.map(({ entity_id, entity: { entity_name } }) => ({
        label: entity_name,
        value: entity_id,
      })) || [],
    [registerShareholderList],
  );

  const handleChangeStatus = useCallback(
    (status: TransactionStatus | null) => {
      setFilters(filters => (status ? { ...filters, status } : omit(filters, 'status')));
    },
    [setFilters],
  );

  const handleChangeRegisterClass = useCallback(
    (register_class_id: number | null) => {
      setFilters(filters =>
        register_class_id ? { ...filters, register_class_id } : omit(filters, 'register_class_id'),
      );
    },
    [setFilters],
  );

  const handleChangeHolder = useCallback(
    (field: keyof transferTabFilters, value: number | null) => {
      setFilters(filters => (value ? { ...filters, [field]: value } : omit(filters, field)));
    },
    [setFilters],
  );

  const handleChangeRequestedDate = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      setFilters(filters =>
        dates && dates[0] && dates[1]
          ? {
              ...filters,
              from_requested_at_date: dates[0].toISOString(),
              to_requested_at_date: dates[1].endOf('d').toISOString(),
            }
          : omit(filters, ['from_requested_at_date', 'to_requested_at_date']),
      );
    },
    [setFilters],
  );

  const handleChangeConfirmationDate = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      setFilters(filters =>
        dates && dates[0] && dates[1]
          ? {
              ...filters,
              from_confirmation_date: dates[0].toISOString(),
              to_confirmation_date: dates[1].endOf('d').toISOString(),
            }
          : omit(filters, ['from_confirmation_date', 'to_confirmation_date']),
      );
    },
    [setFilters],
  );

  return (
    <Transition
      enter="transition duration-100 ease-out"
      enterFrom="transform scale-95 opacity-0"
      enterTo="transform scale-100 opacity-100"
      leave="transition duration-75 ease-out"
      leaveFrom="transform scale-100 opacity-100"
      leaveTo="transform scale-95 opacity-0"
    >
      <Disclosure.Panel static>
        <div className="w-full flex flex-col items-start pb-2.5">
          <div className="w-full flex items-end gap-4 flex-wrap [&>*]:min-w-[230px] [&>*]:w-fit [&>*]:max-w-[300px] [&>*]:flex-1">
            <Select
              isClearable
              label="status"
              placeholder="Any status"
              options={statusOptions}
              value={status}
              onChange={handleChangeStatus}
            />
            <Select
              isClearable
              label="class"
              placeholder="Any class"
              isLoading={isRegisterClassesLoading}
              options={registerClassesOptions}
              value={register_class_id}
              onChange={handleChangeRegisterClass}
            />
            <Select
              isClearable
              label="Holder"
              placeholder="Any holder"
              isLoading={isRegisterShareholderListLoading}
              options={registerShareholderListOptions}
              value={general_shareholder_id}
              onChange={handleChangeHolder.bind(null, 'general_shareholder_id')}
            />
            <Select
              isClearable
              label="from holder"
              placeholder="Any holder"
              isLoading={isRegisterShareholderListLoading}
              options={registerShareholderListOptions}
              value={from_shareholder_id}
              onChange={handleChangeHolder.bind(null, 'from_shareholder_id')}
            />
            <Select
              isClearable
              label="to holder"
              placeholder="Any holder"
              isLoading={isRegisterShareholderListLoading}
              options={registerShareholderListOptions}
              value={to_shareholder_id}
              onChange={handleChangeHolder.bind(null, 'to_shareholder_id')}
            />
            <CalendarInput
              range
              disabledDate={getDisabledDate}
              label="request date"
              className="[&>div]:!py-3 [&>label]:!m-0"
              name="requested_at_date"
              placeholder="Any date"
              rangeValue={
                from_requested_at_date && to_requested_at_date
                  ? [dayjs(from_requested_at_date), dayjs(to_requested_at_date)]
                  : null
              }
              rangeOnChange={handleChangeRequestedDate}
            />
            <CalendarInput
              range
              disabledDate={getDisabledDate}
              label="confirmation date"
              className="[&>div]:!py-3 [&>label]:!m-0"
              name="confirmation_date"
              placeholder="Any date"
              rangeValue={
                from_confirmation_date && to_confirmation_date
                  ? [dayjs(from_confirmation_date), dayjs(to_confirmation_date)]
                  : null
              }
              rangeOnChange={handleChangeConfirmationDate}
            />
          </div>
        </div>
      </Disclosure.Panel>
    </Transition>
  );
};

export default IssuerTransfersFilters;
